import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import AOS from "aos";
import "aos/dist/aos.css";
import { TypeAnimation } from "react-type-animation";
import "./Strategy.css";
import Breadcrumb from "../components/Breadcrumb";

export const dealItems = [
  {
    id: "pe-pc-entry-exit",  // ADDED ID
    title: "PE / PC: Entry and Exit",
    description:
      "Structure and support private equity and private credit transactions from entry through exit, with operating alignment, governance discipline, and value protection.",
    pills: [
      "Entry Strategy",
      "Exit Planning",
      "Governance",
      "Value Protection",
    ],
    bg: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=900&q=80",
    icon: "/assets/service/2.svg",
  },
  {
    id: "ma-corporates",  // ADDED ID
    title: "M&A for Corporates",
    description:
      "Advise strategic buyers and sellers on transaction planning, integration readiness, and execution momentum across deal teams and stakeholders.",
    pills: [
      "Buy-side Advisory",
      "Sell-side Advisory",
      "Integration Readiness",
      "Stakeholder Alignment",
    ],
    bg: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1200",
    icon: "/assets/service/5.svg",
  },
  {
    id: "family-offices",  // ADDED ID
    title: "Investment for Family Offices",
    description:
      "Build capital strategy, portfolio oversight, and governance for family offices seeking disciplined growth, preservation, and generational continuity.",
    pills: [
      "Capital Strategy",
      "Portfolio Oversight",
      "Generational Planning",
      "Governance",
    ],
    bg: "https://images.unsplash.com/photo-1554469384-e58fac16e23a?w=900&q=80",
    icon: "/assets/service/6.svg",
  },
  {
    id: "capital-markets",  // ADDED ID
    title: "Capital Markets",
    description:
      "Support equity and debt market transactions, IPO preparation, listing readiness, and structured access to public and private capital.",
    pills: [
      "IPO Preparation",
      "Listing Readiness",
      "Equity Markets",
      "Debt Structuring",
    ],
    bg: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=900&q=80",
    icon: "/assets/service/1.svg",
  },
];

export const implementationItems = [
  {
    id: "alignment",  
    title: "Alignment",
    description:
      "We align leadership, investors, and operating teams around clear objectives, decisions, and accountabilities so execution is coherent and fast.",
    chips: [
      "Leadership Alignment",
      "Investor Communication",
      "Operating Accountability",
      "Decision Frameworks",
    ],
    bg: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=900&q=80",
    icon: "/assets/service/4.svg",
  },
  {
    id: "business-analysis", 
    title: "Business Analysis",
    description:
      "We combine internal operational review with external market and competitor insight to identify where change will deliver the greatest value.",
    chips: [
      "Operational Review",
      "Market Mapping",
      "Competitor Analysis",
      "Value Identification",
    ],
    bg: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200",
    icon: "/assets/service/7.svg",
  },
  {
    id: "execution", 
    title: "Execution",
    description:
      "We move from plan to outcome with active project discipline, governance, and performance monitoring across priorities and milestones.",
    chips: [
      "Project Discipline",
      "Governance Structure",
      "Performance Monitoring",
      "Milestone Delivery",
    ],
    bg: "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?w=900&q=80",
    icon: "/assets/service/3.svg",
  },
];

const AUTOPLAY_INTERVAL = 3500;

const getSlots = (activeIndex, direction) => {
  const len = implementationItems.length;
  const leftIndex = (activeIndex - 1 + len) % len;
  const rightIndex = (activeIndex + 1) % len;

  return implementationItems.map((_, i) => {
    if (i === activeIndex) return "center";
    if (i === leftIndex) return "left";
    if (i === rightIndex) return "right";
    return direction === "right" ? "hidden-left" : "hidden-right";
  });
};

const Services = () => {
  const navigate = useNavigate();
  const [activeCard, setActiveCard] = useState(0);
  const [activeImpl, setActiveImpl] = useState(0);
  const [slots, setSlots] = useState(() => getSlots(0, "right"));

  const [showPartners, setShowPartners] = useState(false);
  const teamHeaderRef = useRef(null);

  const cardRefs = useRef([]);
  const dealCardRefs = useRef([]);
  const autoplayRef = useRef(null);
  const isAnimating = useRef(false);

  useEffect(() => {
    AOS.init({ duration: 900, once: true, easing: "ease-out-cubic" });
    AOS.refresh();
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });

    AOS.refresh();

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowPartners(true);
        }
      },
      { threshold: 0.4 },
    );

    if (teamHeaderRef.current) {
      observer.observe(teamHeaderRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observers = cardRefs.current.map((el, i) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveCard(i);
        },
        { threshold: 0.55 },
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o && o.disconnect());
  }, []);

  const goTo = useCallback((nextIndex, dir) => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    setActiveImpl(nextIndex);
    setSlots(getSlots(nextIndex, dir));
    setTimeout(() => {
      isAnimating.current = false;
    }, 560);
  }, []);

  const startAutoplay = useCallback(() => {
    clearInterval(autoplayRef.current);
    autoplayRef.current = setInterval(() => {
      setActiveImpl((prev) => {
        const next = (prev + 1) % implementationItems.length;
        setSlots(getSlots(next, "right"));
        return next;
      });
    }, AUTOPLAY_INTERVAL);
  }, []);

  useEffect(() => {
    startAutoplay();
    return () => clearInterval(autoplayRef.current);
  }, [startAutoplay]);

  const handleCardClick = (clickedIndex) => {
    if (isAnimating.current || clickedIndex === activeImpl) return;
    const dir = slots[clickedIndex] === "right" ? "right" : "left";
    goTo(clickedIndex, dir);
    startAutoplay();
  };

  const scrollToDeal = (index) => {
    const el = dealCardRefs.current[index];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setActiveCard(index);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <>
      <Breadcrumb />
      <section className="strategy-section strategy-section--deals">
        <div className="museum-wrap">
          <div className="deals-inner">
            <div className="deals-sticky" data-aos="fade-right">
              <span className="section-label">Our Services</span>
              <h1>
                A Trusted Advisory{" "}
                <span className="highlight">
                  <br/>
                  <TypeAnimation
                    sequence={["Across Every Stage"]}
                    speed={250}
                    cursor={false}
                  />
                </span>
              </h1>

              <p>
                We work across the full spectrum of business challenges: from
                early-stage founders defining their market position, to large
                corporates navigating structural transformation, to family
                offices seeking disciplined capital strategy.
              </p>
              <nav className="deals-service-links" aria-label="Deal types">
                {dealItems.map((item, index) => (
                  <button
                    key={item.title}
                    className={`deals-service-link${activeCard === index ? " deals-service-link--active" : ""}`}
                    onClick={() => scrollToDeal(index)}
                  >
                    <span className="deals-service-link-num">
                      <img 
                        src={item.icon} 
                        alt="" 
                        className="service-icon"
                        width="20"
                        height="20"
                      />
                    </span>
                    <span className="deals-service-link-title">
                      {item.title}
                    </span>
                  </button>
                ))}
              </nav>
            </div>

            <div className="deals-scroll"  data-aos="fade-left">
              {dealItems.map((item, index) => (
                <article
                  key={item.title}
                  className={`deal-card${activeCard === index ? " deal-card--active" : ""}`}
                  ref={(el) => {
                    cardRefs.current[index] = el;
                    dealCardRefs.current[index] = el;
                  }}
                  style={{ backgroundImage: `url('${item.bg}')` }}
                  onClick={() => handleNavigation(`/services/${item.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && handleNavigation(`/services/${item.id}`)}
                  aria-label={`View ${item.title} detail`}
                >
                  <div className="deal-card-overlay" />
                  <div className="deal-card-body">
                    <span className="deal-card-num">
                      <img 
                        src={item.icon} 
                        alt="" 
                        className="service-icon"
                        width="24"
                        height="24"
                      />
                    </span>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <div className="deal-card-cta">
                      Explore 
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── IMPLEMENTATION ── */}
      <section className="strategy-section strategy-section--implementation">
        <div className="museum-wrap">
          <div className="impl-header" data-aos="fade-up" ref={teamHeaderRef}>
            <span className="section-label">Strategy Implementation</span>

            <h2>
              From alignment{" "}
              <span className="highlight" style={{ fontStyle: "italic" }}>
                {showPartners && (
                  <TypeAnimation
                    sequence={["to execution"]}
                    speed={250}
                    cursor={false}
                  />
                )}
              </span>
            </h2>
          </div>
        </div>

        <div className="impl-carousel-wrap" data-aos="fade-up" data-aos-delay="200">
          <div className="impl-stage">
            {implementationItems.map((item, i) => (
              <div
                key={i}
                className={`impl-card impl-card--pos-${slots[i]}`}
                style={{ backgroundImage: `url('${item.bg}')` }}
                onClick={() => handleCardClick(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCardClick(i);
                }}
                aria-label={`View ${item.title} details`}
              >
                <div className="impl-card-overlay" />
                <div className="impl-card-body">
                  <div className="impl-card-idx">
                    <img 
                      src={item.icon} 
                      alt="" 
                      className="service-icon"
                      width="24"
                      height="24"
                    />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <div 
                    className="deal-card-cta"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigation(`/services/${item.id}`);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') handleNavigation(`/services/${item.id}`);
                    }}
                    aria-label={`View ${item.title} detail`}
                  >
                    Explore 
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dot indicators */}
          <div className="impl-dots">
            {implementationItems.map((_, i) => (
              <button
                key={i}
                className={`impl-dot${i === activeImpl ? " impl-dot--active" : ""}`}
                onClick={() => {
                  if (i !== activeImpl) {
                    const dir = i > activeImpl ? "right" : "left";
                    goTo(i, dir);
                    startAutoplay();
                  }
                }}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;